import useScrollIntoView from '@okam/brand-ui/helpers/useScrollIntoView'
import { PageTitle } from '@okam/brand-ui/index'
import { useHtml2React } from '@okam/html2react/index'
import type { MDXRemoteSerializeResult } from 'next-mdx-remote'
import { css } from 'twin.macro'
import { MainLayout } from 'components/layout'
import type { TDirectusProps } from 'components/layout/MainLayout'
import AnchorsBlock from 'components/page-blocks/AnchorsBlock/AnchorsBlock'
import PetParentsCornerBlockDispatcher from 'components/page-blocks/pet-parents-corner-blocks/PetParentsCornerBlockDispatcher/PetParentsCornerBlockDispatcher'
import type { TAnchor } from 'components/page-blocks/types'
import Seo from 'components/Seo'
import { useFragment } from 'gql'
import type { ColorSchemeFragment, SeoContentFragment } from 'gql/graphql'
import { useDirectus } from 'lib/directus'
import { usePetParentsCornerPage } from 'lib/react-query'
import { ThemeContextProvider } from 'providers/Color'
import type { TColorScheme } from 'providers/Color/interface'
import { colorSchemeFragment, pageSectionsFragment } from 'query/single-page/fragment'

export interface TPetParentsCornerPage {
  locale: string
  mdxSources: Array<MDXRemoteSerializeResult<Record<string, unknown>, Record<string, unknown>>>
  seo: { seo: SeoContentFragment | null | undefined; pageTitle: string }
  directusProps: TDirectusProps
}

const PetParentsCornerPage = ({ locale, mdxSources, seo, directusProps }: TPetParentsCornerPage) => {
  const { data: pageData, isSuccess: pageSuccess } = usePetParentsCornerPage(locale)

  const pageColors = useFragment<ColorSchemeFragment>(colorSchemeFragment, pageData?.colorScheme)

  const anchors: TAnchor[] = []
  const anchoredSections: string[] = []

  const pageFrTranslations = pageData?.frTranslations?.[0]

  pageData?.translations?.[0]?.sections?.forEach((section, index) => {
    /* eslint-disable-next-line */
    const typedSection = useFragment(pageSectionsFragment, section)
    /* eslint-disable-next-line */
    const typedFrSection = useFragment(pageSectionsFragment, pageFrTranslations?.sections?.[index])

    if (typedSection?.item?.isAnchored) {
      anchors.push({
        label: typedSection?.item?.shortTitle ?? '',
        frLabel: typedFrSection?.item?.shortTitle ?? '',
        refIndex: anchors.length,
      })
      anchoredSections.push(typedSection.id)
    }
  })

  const footerProps = useDirectus()

  const backgroundColor = pageColors?.background

  const [refs, scrollToRef] = useScrollIntoView<HTMLDivElement, TAnchor>(anchors)

  const { parseHTMLToReact } = useHtml2React()

  let mdxBlockIndex = -1
  let anchorIndex = -1

  return (
    <>
      <Seo directusData={directusProps} seo={seo?.seo} pageTitle={seo?.pageTitle} />
      <MainLayout footerProps={footerProps} logoColor={pageColors?.accent} tw="overflow-x-hidden">
        <div
          css={[
            css`
              background-color: ${backgroundColor};
            `,
          ]}
        >
          {pageSuccess && (
            <ThemeContextProvider colorScheme={pageData.colorScheme as TColorScheme}>
              <div
                css={[
                  css`
                    background-color: ${backgroundColor};
                  `,
                ]}
              >
                <PageTitle>{parseHTMLToReact(pageData?.translations?.[0]?.title ?? '')}</PageTitle>
              </div>
            </ThemeContextProvider>
          )}
          <AnchorsBlock anchors={anchors} scrollToRef={scrollToRef} isDirectusAnchors={false} />
          {pageData?.translations?.[0]?.sections?.map((section, index) => {
            if (section?.collection === 'block_mdx') {
              mdxBlockIndex += 1
            }
            if (anchoredSections.includes(section?.id ?? '')) {
              anchorIndex += 1
            }

            return (
              <PetParentsCornerBlockDispatcher
                key={section?.id}
                anchors={anchors}
                anchorIndex={anchorIndex}
                section={section}
                refs={refs}
                mdxBlockIndex={mdxBlockIndex}
                mdxSources={mdxSources}
                frTranslations={pageFrTranslations?.sections?.[index]}
              />
            )
          })}
        </div>
      </MainLayout>
    </>
  )
}
export default PetParentsCornerPage
